import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import io from "socket.io-client";
import confetti from "canvas-confetti";
import {
  ChevronDown,
  ChevronRight,
  Share2,
  Copy,
  Crown,
  Dice5,
  User,
} from "lucide-react";

import GameBoard from "../GameBoard/GameBoard";
import { motion, AnimatePresence } from "framer-motion";
import "../GameDetail/GameDetail.css";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/public`;

// Initialize socket outside the component to prevent multiple connections
const socket = io(process.env.REACT_APP_API_BASE_URL);

const getContrastTextColor = (hexColor) => {
  const hex = hexColor.startsWith("#") ? hexColor.slice(1) : hexColor;
  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((char) => char + char)
          .join("")
      : hex;
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 160 ? "#000000" : "#FFFFFF";
};

const showConfettiFireworks = () => {
  const duration = 15 * 1000;
  const animationEnd = Date.now() + duration;
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };

  const randomInRange = (min, max) => Math.random() * (max - min) + min;

  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();
    if (timeLeft <= 0) return clearInterval(interval);

    const particleCount = 50 * (timeLeft / duration);
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
    });
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
    });
  }, 250);
};

const getOrdinal = (n) => {
  if (typeof n !== "number") return "N/A";
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const formatCountdown = (ms) => {
  if (ms <= 0) return "00:00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((v) => (v < 10 ? "0" + v : v))
    .filter((v, i) => v !== "00" || i > 0)
    .join(":");
};

function SpectateGameDetail() {
  const { gameId } = useParams();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameLog, setGameLog] = useState([]);
  const [expandedTeams, setExpandedTeams] = useState(new Set());
  const [isEventStarted, setIsEventStarted] = useState(false);
  const [countdownText, setCountdownText] = useState("");
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [selectedFilterTeam, setSelectedFilterTeam] = useState("All");
  const [showProgressionLines, setShowProgressionLines] = useState(false);

  const handleCopyLink = () => {
    const spectatorUrl = `${window.location.origin}/games/spectate/${gameId}`;
    navigator.clipboard.writeText(spectatorUrl).then(() => {
      setShowCopyTooltip(true);
      setTimeout(() => setShowCopyTooltip(false), 3000);
    });
  };

  const filteredLogs = useMemo(() => {
    if (selectedFilterTeam === "All") return gameLog;
    return gameLog.filter((log) => log.team_name === selectedFilterTeam);
  }, [gameLog, selectedFilterTeam]);

  const toggleTeamExpansion = (teamId) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  };

  const fetchGameDetails = useCallback(async () => {
    try {
      const gameUrl = `${API_BASE_URL}/game/${gameId}`;
      const gameResponse = await axios.get(gameUrl);
      const gameData = gameResponse.data.game;
      setGame(gameData);
      setGameLog(gameData.log_entries || []);
      setError(null);
    } catch (err) {
      setError(
        "Failed to load game details. It may not exist or is no longer public.",
      );
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    fetchGameDetails();
  }, [fetchGameDetails]);

  useEffect(() => {
    if (!gameId) return;
    socket.emit("join_game", { game_id: gameId });

    const handleGameStateUpdate = (data) => {
      setGame((prevGame) =>
        prevGame ? { ...prevGame, ...data.game_state } : null,
      );
    };

    const handleNewLogEntry = (data) => {
      setGameLog((prevLog) => [...prevLog, data.log_entry]);
    };

    socket.on("game_state_update", handleGameStateUpdate);
    socket.on("tasks_reordered", handleGameStateUpdate);
    socket.on("new_log_entry", handleNewLogEntry);

    return () => {
      socket.emit("leave_game", { game_id: gameId });
      socket.off("game_state_update", handleGameStateUpdate);
      socket.off("tasks_reordered", handleGameStateUpdate);
      socket.off("new_log_entry", handleNewLogEntry);
    };
  }, [gameId]);

  useEffect(() => {
    if (!game?.start_datetime) return;
    const updateTimer = () => {
      const dateString =
        game.start_datetime.endsWith("Z") || game.start_datetime.includes("+")
          ? game.start_datetime
          : `${game.start_datetime}Z`;
      const diff = new Date(dateString).getTime() - new Date().getTime();
      if (diff <= 0) {
        setIsEventStarted(true);
        setCountdownText("");
      } else {
        setIsEventStarted(false);
        setCountdownText(formatCountdown(diff));
      }
    };
    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [game?.start_datetime]);

  const winnersDataList = useMemo(() => {
    try {
      if (game?.winners) {
        const parsedWinners = JSON.parse(game.winners);
        return Array.isArray(parsedWinners) ? parsedWinners : [parsedWinners];
      }
    } catch (e) {
      console.error("Error parsing game.winners JSON:", e);
    }
    return [];
  }, [game?.winners]);

  const currentWinnerCount = winnersDataList.length;
  const latestWinnerData = winnersDataList[currentWinnerCount - 1] || null;
  const gameCompleted = game?.status === "completed";

  useEffect(() => {
    if (currentWinnerCount > 0) {
      showConfettiFireworks();
    }
  }, [currentWinnerCount]);

  if (loading)
    return (
      <div className="game-status game-loading-message">
        Loading Spectate View...
      </div>
    );
  if (error) return <div className="game-status error-message">{error}</div>;
  if (!game)
    return <div className="game-status not-found">Game not found.</div>;

  const currentTitle = game.event_name || `Game ${gameId}`;
  const getStatusClass = (status) =>
    status ? `status-${status.toLowerCase().replace(/[^a-z0-9]/g, "-")}` : "";

  return (
    <div className="game-detail spectate-view">
      <div className="game-header-container">
        <h2>
          <span className="clan-prefix">{game.clan_name}</span>
          <span className="title-separator"> / </span>
          {game.event_type || "Game"}: {currentTitle} (Spectator View)
          <button
            className="btn-share-link"
            onClick={handleCopyLink}
            title="Share Game"
          >
            <Share2 size={20} />
          </button>
        </h2>
      </div>

      <div className="game-status-info">
        <p className="game-summary game-summary-text">
          Status:&nbsp;
          <strong
            className={`game-status-badge ${!isEventStarted && game.status === "in-progress" ? "status-upcoming" : getStatusClass(game.status)}`}
          >
            {!isEventStarted && game.status === "in-progress"
              ? "UPCOMING"
              : game.status}
          </strong>
          &nbsp;| Board Size: <strong>{game.board_size}</strong>
        </p>

        {!isEventStarted &&
          !["completed", "finished"].includes(game.status) && (
            <div className="event-timer-container">
              <div className="countdown-timer-text">
                Starts in: {countdownText}
              </div>
              <span className="local-start-time">
                Local Start:{" "}
                {new Date(game.start_datetime + "Z").toLocaleString([], {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </span>
            </div>
          )}

        {isEventStarted && game.status === "in-progress" && (
          <div className="live-indicator">
            <span className="pulse-dot">●</span> LIVE
          </div>
        )}
      </div>

      <div className="game-main-content">
        <div className="game-board-container">
          <GameBoard
            game={game}
            selectedFilterTeam={selectedFilterTeam}
            showProgressionLines={showProgressionLines}
          />
        </div>

        <div className="game-sidebar">
          {latestWinnerData && !gameCompleted && (
            <p
              className="game-status-message winner-message latest-winner-announcement"
              style={{
                backgroundColor: latestWinnerData.color || "#27ae60",
                color: getContrastTextColor(
                  latestWinnerData.color || "#27ae60",
                ),
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              🎉 Rank Secured! {getOrdinal(latestWinnerData.rank)} Place:{" "}
              <strong>Team {latestWinnerData.name}</strong>!
            </p>
          )}

          {gameCompleted && (
            <p
              className="game-status-message status-completed winner-message"
              style={{ textAlign: "center", marginBottom: "20px" }}
            >
              Game Over! All final ranks have been secured.
            </p>
          )}

          {currentWinnerCount > 0 && (
            <div className="leaderboard-section">
              <h3>Ranks Secured ({currentWinnerCount})</h3>
              <ul className="team-positions-list">
                {winnersDataList.map((winner) => (
                  <li
                    key={winner.id}
                    className="winner-rank-item"
                    data-rank={winner.rank}
                    style={{ borderLeftColor: winner.color }}
                  >
                    <strong>{getOrdinal(winner.rank)} Place:</strong>
                    <span style={{ color: winner.color }}> {winner.name}</span>
                  </li>
                ))}
              </ul>
              <hr
                className="sidebar-hr"
                style={{ display: "block", margin: "20px 0" }}
              />
            </div>
          )}

          <div className="team-positions-header-container">
            <h3>Team Positions</h3>
            <span className="max-reverts-label">
              (Max reverts allowed: {game.max_reverts_allowed})
            </span>
          </div>

          <ul className="team-positions-list" style={{ position: "relative" }}>
            <AnimatePresence mode="popLayout">
              {[...game.team_states]
                .sort((a, b) => b.current_position - a.current_position)
                .map((state, index, allTeams) => {
                  const isExpanded = expandedTeams.has(state.team_id);
                  const hasMembers = (state.members?.length || 0) > 0;
                  return (
                    <motion.li
                      layout
                      key={state.team_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        zIndex: allTeams.length - index,
                      }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="team-position-item"
                      style={{ borderLeftColor: state.team_color }}
                    >
                      <motion.div
                        layout="position"
                        className={`team-header ${hasMembers ? "has-members" : "empty-team"}`}
                        onClick={() =>
                          hasMembers && toggleTeamExpansion(state.team_id)
                        }
                        style={{
                          height: "50px",
                          display: "flex",
                          alignItems: "center",
                          cursor: hasMembers ? "pointer" : "default",
                        }}
                      >
                        <div className="team-header-left">
                          <span className="team-info-summary">
                            <strong style={{ color: state.team_color }}>
                              {state.team_name}
                            </strong>
                            <span className="team-stats-inline">
                              <span className="position-text">
                                Pos: <strong>{state.current_position}</strong>
                              </span>
                              <span
                                className={`revert-tag ${state.revert_count >= game.max_reverts_allowed ? "at-limit" : ""}`}
                              >
                                ({state.revert_count} /{" "}
                                {game.max_reverts_allowed} reverts)
                              </span>
                            </span>
                          </span>
                        </div>
                        <div className="team-header-right-group">
                          <span className="team-member-count">
                            {state.members?.length || 0}{" "}
                            {state.members?.length === 1 ? "Member" : "Members"}
                          </span>
                          <div
                            className="expansion-indicator"
                            style={{ opacity: hasMembers ? 1 : 0.3 }}
                          >
                            {isExpanded && hasMembers ? (
                              <ChevronDown size={16} strokeWidth={3} />
                            ) : (
                              <ChevronRight
                                size={16}
                                strokeWidth={2}
                                opacity={0.6}
                              />
                            )}
                          </div>
                        </div>
                      </motion.div>

                      {hasMembers && (
                        <ul
                          className={`team-members-list ${!isExpanded ? "collapsed" : ""}`}
                        >
                          {[...(state.members || [])]
                            .sort((a, b) => {
                              const roles = { Leader: 3, Roller: 2, Member: 1 };
                              return (
                                (roles[b.role] || 0) - (roles[a.role] || 0)
                              );
                            })
                            .map((member, idx) => (
                              <li
                                key={idx}
                                className={`member-item role-${member.role.toLowerCase()}`}
                              >
                                <span className="member-role-indicator">
                                  {member.role === "Leader" ? (
                                    <Crown
                                      size={14}
                                      className="role-icon leader"
                                    />
                                  ) : member.role === "Roller" ? (
                                    <Dice5
                                      size={14}
                                      className="role-icon roller"
                                    />
                                  ) : (
                                    <User
                                      size={14}
                                      className="role-icon member"
                                    />
                                  )}
                                </span>
                                <span className="member-name">
                                  {member.ign}
                                </span>
                                <span className="member-role-label">
                                  {member.role}
                                </span>
                              </li>
                            ))}
                        </ul>
                      )}
                    </motion.li>
                  );
                })}
            </AnimatePresence>
          </ul>

          <hr className="sidebar-hr" />

          <div className="game-log-container">
            <div className="log-header-row">
              <h3>
                Live Game Log <span className="utc-label">(in UTC time)</span>
              </h3>
              <div className="log-controls">
                <select
                  className="log-filter-select"
                  value={selectedFilterTeam}
                  onChange={(e) => setSelectedFilterTeam(e.target.value)}
                >
                  <option value="All">All Teams</option>
                  {game.team_states.map((s) => (
                    <option key={s.team_id} value={s.team_name}>
                      {s.team_name}
                    </option>
                  ))}
                </select>
                <label className="progression-toggle">
                  <input
                    type="checkbox"
                    checked={showProgressionLines}
                    onChange={(e) => setShowProgressionLines(e.target.checked)}
                  />
                  Show Paths
                </label>
              </div>
            </div>
            <ul className="game-log">
              {filteredLogs
                .slice(-100)
                .reverse()
                .map((log, index) => (
                  <li key={log.id || index} className="log-entry">
                    [{new Date(log.timestamp).toLocaleTimeString()}]&nbsp;
                    <strong
                      style={{
                        color:
                          log.team_name === "Server"
                            ? "#e67e22"
                            : log.team_name === "Admin"
                              ? "#f1c40f"
                              : log.team_color,
                      }}
                    >
                      {log.team_name}:
                    </strong>
                    &nbsp;{log.message}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
      {showCopyTooltip && (
        <div className="copy-toast">
          <Copy size={16} /> <span>Spectator link copied to clipboard!</span>
        </div>
      )}
    </div>
  );
}

export default SpectateGameDetail;
